import { logout } from "@/api/auth";
import { setForbiddenHandler, setReloginHandler, setUnauthorizedHandler } from "@/api/client";
import {
  clearCachedSession,
  clearSession,
  currentOwnerKey,
  getToken,
  setToken,
} from "@/api/session";
import { flushOutbox, releaseOutbox } from "@/offline";
import { UnlockGate } from "@/screens/unlock/UnlockGate";
import { useQueryClient } from "@tanstack/react-query";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { attemptRelogin, forgetRememberedSecret, resumeRelogin } from "./relogin";
import { ME_QUERY_KEY } from "./session";
import { wipeVault } from "./vault";

interface AuthContextValue {
  token: string | null;
  signIn: (token: string) => void;
  signOut: () => void;
  signOutFromDevice: () => Promise<void>;
  resumeSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [token, setTokenState] = useState<string | null>(() => getToken());

  const forget = useCallback(() => {
    releaseOutbox();
    forgetRememberedSecret();
    clearSession();
    setTokenState(null);
    queryClient.clear();
  }, [queryClient]);

  const signOutFromDevice = useCallback(async () => {
    const ownerKey = currentOwnerKey();
    await logout().catch(() => undefined);
    await wipeVault(ownerKey);
    forget();
  }, [forget]);

  const resumeSession = useCallback(async () => {
    resumeRelogin();
    const outcome = await attemptRelogin();
    if (outcome.status === "renewed") {
      setTokenState(outcome.token);
      void flushOutbox();
      await queryClient.invalidateQueries();
      return;
    }
    if (outcome.status === "unavailable" || outcome.status === "rejected") {
      forget();
    }
  }, [forget, queryClient]);

  useEffect(() => {
    setUnauthorizedHandler(forget);
    setReloginHandler(async () => {
      const outcome = await attemptRelogin();
      if (outcome.status === "renewed") {
        setTokenState(outcome.token);
      }
      return outcome.status;
    });
    setForbiddenHandler(() => queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }));
  }, [forget, queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      signIn: (next: string) => {
        resumeRelogin();
        setToken(next);
        clearCachedSession();
        setTokenState(next);
        queryClient.clear();
        void flushOutbox();
      },
      signOut: forget,
      signOutFromDevice,
      resumeSession,
    }),
    [token, forget, signOutFromDevice, resumeSession, queryClient],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <UnlockGate />
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const auth = useContext(AuthContext);
  if (!auth) {
    throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  }
  return auth;
}
